import React from 'react';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from "@mui/icons-material";
import {
  Box,
  TableCell,
  TableRow,
  Typography,
  Collapse,
  IconButton,
  useTheme,
} from '@mui/material';

import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import {
  MergedHistory,
  HistoryType,
  toStringHistoryType,
  toStringAction,
} from '../../../types/models/MergedHistory';
import { displayDateTime } from '../../../../utils/formatDate.ts';
import { fixedFormat } from '../../../../utils/numberFormat';
import { toStringDisplay as toStringShipmentState } from '../../../types/models/ShipmentState.d';
import { toStringDisplay as toStringShipmentType } from '../../../types/models/ShipmentType.d';
import { toStringDisplay as toStringCordwoodMeasurementType } from '../../../types/models/CordwoodMeasurementType.d';
import CuttingName from '../../cutting/CuttingDetail/CuttingName';
import TimberStorageName from '../../timberStorage/TimberStorageDetail/TimberStorageName';
import BuyerName from '../../buyer/BuyerDetail/BuyerName';
import ShipmentName from '../../shipment/ShipmentDetail/ShipmentName';
import LogLink from '../../log/LogDetail/LogLink';
import CordwoodLink from '../../cordwood/CordwoodDetail/CordwoodLink';
import ShipmentLink from '../../shipment/ShipmentDetail/ShipmentLink';

interface HistoryListItemRowProps {
  row: MergedHistory;
}

const getHistoryTypeColor = (type: HistoryType): string => {
  switch (type) {
    case 'Shipment':
      return '#1976d2';
    case 'Log':
      return '#2e7d32';
    case 'Cordwood':
      return '#ed6c02';
    default:
      return '#757575';
  }
};

const getActionColor = (action: string): string => {
  if (action.includes('create')) return '#2e7d32';
  if (action.includes('update')) return '#1976d2';
  if (action.includes('delete')) return '#d32f2f';
  if (action.includes('cancel')) return '#ed6c02';
  return '#757575';
};

const getVolumeDisplay = (row: MergedHistory): string => {
  if (row.historyType === 'Shipment') {
    const stateStr = row.state ? `(${toStringShipmentState(row.state)})` : '';
    const typeStr = row.shipmentType
      ? `(${toStringShipmentType(row.shipmentType)})`
      : '';
    return row.shipmentType ? `${typeStr} ${stateStr}` : stateStr || '-';
  }
  if (row.volume === undefined || row.volume === null) {
    return '-';
  }
  if (row.historyType === 'Log' && row.length && row.diameter) {
    return `${fixedFormat(row.volume)}m³ (${fixedFormat(row.length)} × ${fixedFormat(row.diameter)})`;
  }
  let volumeStr = `${fixedFormat(row.volume)}m³`;
  if (row.measurementType) {
    volumeStr += ` (${toStringCordwoodMeasurementType(row.measurementType)})`;
  }
  return volumeStr;
};

const EntityKeyLink = ({ row }: { row: MergedHistory }) => {
  if (!row.entityId) {
    return row.key || '-';
  }

  switch (row.historyType) {
    case 'Shipment':
      return <ShipmentLink id={row.entityId} />;
    case 'Log':
      return <LogLink id={row.entityId} />;
    case 'Cordwood':
      return <CordwoodLink id={row.entityId} />;
    default:
      return row.key || '-';
  }
};

const HistoryListItemRow = ({ row }: HistoryListItemRowProps) => {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const renderExpandedDetails = () => {
    const leftColumnMeta = [];
    const rightColumnData = [];

    if (row.historyType === 'Shipment') {
      leftColumnMeta.push(
        { label: <Trans>Report Key</Trans>, value: row.reportKey },
        {
          label: <Trans>Cutting</Trans>,
          value: row.cuttingId ? <CuttingName id={row.cuttingId} /> : null,
        },
        {
          label: <Trans>Timber Storage</Trans>,
          value: row.storageId ? (
            <TimberStorageName id={row.storageId} />
          ) : null,
        },
        {
          label: <Trans>Buyer</Trans>,
          value: row.buyerId ? <BuyerName id={row.buyerId} /> : null,
        },
      );
      rightColumnData.push({
        label: <Trans>State</Trans>,
        value: row.state ? toStringShipmentState(row.state) : '-',
      });
    }
    if (row.historyType === 'Log') {
      leftColumnMeta.push(
        { label: <Trans>Timber Key</Trans>, value: row.key },
        {
          label: <Trans>Cutting</Trans>,
          value: row.cuttingId ? <CuttingName id={row.cuttingId} /> : null,
        },
        {
          label: <Trans>Timber Storage</Trans>,
          value: row.storageId ? (
            <TimberStorageName id={row.storageId} />
          ) : null,
        },
        {
          label: <Trans>Shipment</Trans>,
          value: row.shipmentId ? <ShipmentName id={row.shipmentId} /> : null,
        },
        {
          label: <Trans>Committed</Trans>,
          value: row.commited ? t`Yes` : t`No`,
        },
      );
      rightColumnData.push(
        { label: <Trans>Volume</Trans>, value: getVolumeDisplay(row) },
        {
          label: <Trans>Length</Trans>,
          value: row.length ? `${fixedFormat(row.length)} m` : '-',
        },
        {
          label: <Trans>Diameter</Trans>,
          value: row.diameter ? `${fixedFormat(row.diameter)} cm` : '-',
        },
        { label: <Trans>Linked</Trans>, value: row.linked ? t`Yes` : t`No` },
      );
    }
    if (row.historyType === 'Cordwood') {
      leftColumnMeta.push(
        { label: <Trans>Timber Key</Trans>, value: row.key },
        {
          label: <Trans>Cutting</Trans>,
          value: row.cuttingId ? <CuttingName id={row.cuttingId} /> : null,
        },
        {
          label: <Trans>Timber Storage</Trans>,
          value: row.storageId ? (
            <TimberStorageName id={row.storageId} />
          ) : null,
        },
        {
          label: <Trans>Shipment</Trans>,
          value: row.shipmentId ? <ShipmentName id={row.shipmentId} /> : null,
        },
        {
          label: <Trans>Committed</Trans>,
          value: row.commited ? t`Yes` : t`No`,
        },
      );
      rightColumnData.push({
        label: <Trans>Volume</Trans>,
        value: getVolumeDisplay(row),
      });
    }

    const renderMetaItem = (
      item: { label: React.ReactNode; value: React.ReactNode },
      index: number,
    ) => {
      if (item.value === null || item.value === undefined) return null;
      return (
        <Typography key={index} variant="body2">
          <strong>{item.label}:</strong> {item.value}
        </Typography>
      );
    };

    return (
      <Box
        sx={{
          p: 2,
          backgroundColor:
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {leftColumnMeta.map(renderMetaItem)}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {rightColumnData.map(renderMetaItem)}
          </Box>
        </Box>
        {row.contextHistory && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>
              <Trans>Context</Trans>:
            </strong>{' '}
            {row.contextHistory}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{ '& > *': { borderBottom: 'unset' } }}
        data-testid={`History-row-${row.id}`}
      >
        <TableCell sx={{ width: 50 }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            data-testid={`History-row-expand-${row.id}`}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell data-testid={`History-row-updatedAt-${row.id}`}>
          {displayDateTime(row.updatedAt)}
        </TableCell>
        <TableCell data-testid={`History-row-historyType-${row.id}`}>
          <Box
            component="span"
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              backgroundColor: getHistoryTypeColor(row.historyType),
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 'bold',
            }}
          >
            {toStringHistoryType(row.historyType)}
          </Box>
        </TableCell>
        <TableCell data-testid={`History-row-action-${row.id}`}>
          <Box
            component="span"
            sx={{
              color: getActionColor(row.action),
              fontWeight: 'medium',
            }}
          >
            {toStringAction(row.action)}
          </Box>
        </TableCell>
        <TableCell data-testid={`History-row-key-${row.id}`}>
          <EntityKeyLink row={row} />
        </TableCell>
        <TableCell data-testid={`History-row-volume-${row.id}`}>
          {getVolumeDisplay(row)}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box data-testid={`History-row-details-${row.id}`}>
              {renderExpandedDetails()}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};

export default HistoryListItemRow;
